import { router, usePathname } from 'expo-router';
import {
  Bell,
  CircleUserRound,
  Globe,
  Plus,
  Users,
  type LucideIcon,
} from 'lucide-react-native';
import { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAuth } from '@/src/features/auth/use-auth';
import { useUnreadNotificationCount } from '@/src/features/notifications/use-notifications';
import { CreatePostModal } from '@/src/features/posts/CreatePostModal';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { scrollTabToTop } from '@/src/utils/tab-scroll';

export const APP_BOTTOM_TAB_BAR_BASE_HEIGHT = 76;

const MAX_WEB_BOTTOM_TAB_INSET = 16;

const fixedTabBarStyle =
  Platform.OS === 'web'
    ? ({
        bottom: 0,
        left: 0,
        position: 'fixed',
        right: 0,
        zIndex: 40,
      } as unknown as ViewStyle)
    : ({
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        zIndex: 40,
      } satisfies ViewStyle);

export type AuthenticatedTabRoute =
  | 'friends'
  | 'home'
  | 'notifications'
  | 'profile';

type AuthenticatedBottomTabBarProps = {
  activeRouteName?: string;
  fixed?: boolean;
  onNavigate?: (routeName: AuthenticatedTabRoute) => void;
};

type TabItem = {
  Icon: LucideIcon;
  href: `/${string}`;
  label: string;
  routeName: AuthenticatedTabRoute;
};

const TAB_ITEMS: TabItem[] = [
  {
    Icon: Globe,
    href: '/home',
    label: 'Public',
    routeName: 'home',
  },
  {
    Icon: Users,
    href: '/friends',
    label: 'Frienzzone',
    routeName: 'friends',
  },
  {
    Icon: Bell,
    href: '/notifications',
    label: 'Notifications',
    routeName: 'notifications',
  },
  {
    Icon: CircleUserRound,
    href: '/profile',
    label: 'Profile',
    routeName: 'profile',
  },
];

function tabRouteForPathname(pathname: string): AuthenticatedTabRoute | null {
  if (pathname === '/home') {
    return 'home';
  }

  if (pathname === '/friends') {
    return 'friends';
  }

  if (pathname === '/notifications') {
    return 'notifications';
  }

  if (pathname === '/profile') {
    return 'profile';
  }

  if (pathname.startsWith('/users/')) {
    return 'profile';
  }

  return null;
}

export function getStableBottomInset(bottomInset: number) {
  const inset = Math.max(bottomInset, spacing.sm);

  if (Platform.OS === 'web') {
    return Math.min(inset, MAX_WEB_BOTTOM_TAB_INSET);
  }

  return inset;
}

function TabIcon({
  active,
  color,
  Icon,
}: {
  active: boolean;
  color: string;
  Icon: LucideIcon;
}) {
  return <Icon color={color} size={24} strokeWidth={active ? 2.7 : 2.4} />;
}

function AppTabButton({
  active,
  badge,
  Icon,
  label,
  onPress,
}: {
  active: boolean;
  badge?: number | string;
  Icon: LucideIcon;
  label: string;
  onPress: () => void;
}) {
  const color = active ? colors.primary : colors.mutedText;
  const badgeLabel = badge == null ? '' : String(badge);
  const compactBadge = badgeLabel.length <= 2;

  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.tabButton,
        pressed ? styles.tabButtonPressed : null,
      ]}
    >
      <View>
        <TabIcon Icon={Icon} active={active} color={color} />
        {badge ? (
          <View
            style={[
              styles.badge,
              compactBadge ? styles.badgeCompact : styles.badgeWide,
            ]}
          >
            <Text style={styles.badgeText}>{badgeLabel}</Text>
          </View>
        ) : null}
      </View>
      <Text
        adjustsFontSizeToFit
        minimumFontScale={0.78}
        numberOfLines={1}
        style={[styles.tabLabel, { color }]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function AuthenticatedBottomTabBar({
  activeRouteName,
  fixed = false,
  onNavigate,
}: AuthenticatedBottomTabBarProps) {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const { user } = useAuth();
  const unreadNotificationCount = useUnreadNotificationCount(user?.uid);
  const notificationBadge =
    unreadNotificationCount > 0
      ? unreadNotificationCount > 99
        ? '99+'
        : unreadNotificationCount
      : undefined;
  const bottomInset = getStableBottomInset(insets.bottom);
  const tabBarHeight = APP_BOTTOM_TAB_BAR_BASE_HEIGHT + bottomInset;
  const activeTabRoute =
    activeRouteName && TAB_ITEMS.some((item) => item.routeName === activeRouteName)
      ? (activeRouteName as AuthenticatedTabRoute)
      : tabRouteForPathname(pathname);

  function navigateToTab(item: TabItem) {
    if (activeTabRoute === item.routeName && !onNavigate) {
      scrollTabToTop(item.routeName);
    }

    if (onNavigate) {
      onNavigate(item.routeName);
      return;
    }

    router.push(item.href);
  }

  return (
    <>
      <View
        style={[
          styles.tabBar,
          fixed ? fixedTabBarStyle : null,
          {
            height: tabBarHeight,
            paddingBottom: bottomInset,
          },
        ]}
      >
        <AppTabButton
          active={activeTabRoute === 'home'}
          Icon={Globe}
          label="Public"
          onPress={() => navigateToTab(TAB_ITEMS[0])}
        />
        <AppTabButton
          active={activeTabRoute === 'friends'}
          Icon={Users}
          label="Frienzzone"
          onPress={() => navigateToTab(TAB_ITEMS[1])}
        />
        <View pointerEvents="none" style={styles.createTabSlot} />
        <AppTabButton
          active={activeTabRoute === 'notifications'}
          Icon={Bell}
          badge={notificationBadge}
          label="Notifications"
          onPress={() => navigateToTab(TAB_ITEMS[2])}
        />
        <AppTabButton
          active={activeTabRoute === 'profile'}
          Icon={CircleUserRound}
          label="Profile"
          onPress={() => navigateToTab(TAB_ITEMS[3])}
        />
        <Pressable
          accessibilityLabel="Create post"
          accessibilityRole="button"
          style={({ pressed }) => [
            styles.createButton,
            pressed ? styles.createButtonPressed : null,
          ]}
          onPress={() => setCreateModalVisible(true)}
        >
          <Plus color={colors.surface} size={34} strokeWidth={2.8} />
        </Pressable>
      </View>
      <CreatePostModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.surface,
    borderRadius: 10,
    borderWidth: 2,
    height: 20,
    justifyContent: 'center',
    position: 'absolute',
    right: -10,
    top: -7,
  },
  badgeCompact: {
    width: 20,
  },
  badgeText: {
    color: colors.surface,
    fontSize: 10,
    fontWeight: '900',
  },
  badgeWide: {
    minWidth: 28,
    paddingHorizontal: 4,
  },
  createButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.surface,
    borderRadius: 32,
    borderWidth: 4,
    height: 64,
    justifyContent: 'center',
    left: '50%',
    marginLeft: -32,
    position: 'absolute',
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.28,
    shadowRadius: 18,
    top: -12,
    width: 64,
  },
  createButtonPressed: {
    backgroundColor: colors.primaryPressed,
    transform: [{ scale: 0.98 }],
  },
  createTabSlot: {
    alignItems: 'center',
    flex: 1,
    height: 58,
    justifyContent: 'center',
  },
  tabBar: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: spacing.sm,
    position: 'relative',
  },
  tabButton: {
    alignItems: 'center',
    flex: 1,
    gap: spacing.xs,
    height: 58,
    justifyContent: 'center',
    minWidth: 0,
  },
  tabButtonPressed: {
    opacity: 0.72,
  },
  tabLabel: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16,
    maxWidth: '100%',
    paddingHorizontal: 2,
  },
});
